import { Badge, Box, Dialog, Flex, Heading, Text } from '@radix-ui/themes';
import type { AudiencePlatformItem, CountryItem } from '@digiseg-labs/api-client';
import { useMemo } from 'react';
import AudienceRequestForm from '../AudienceRequestForm';
import AudienceBuilderAnalysis from './AudienceBuilderAnalysis';
import GenericSelect from '../GenericSelect';
import { ViewHorizontalIcon, GlobeIcon } from '@radix-ui/react-icons';
import type { ReachAnalysis, SankeyLink, SankeyNode, SelectedAudience } from './types';

const escapeHtml = (value: string) => (
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
);

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedAudiences: SelectedAudience[];
  countries: CountryItem[];
  selectedCountry: CountryItem | undefined;
  onCountryChange: (country: CountryItem | undefined) => void;
  platforms: AudiencePlatformItem[];
  analysis: ReachAnalysis;
  sankeyLinks: SankeyLink[];
  sankeyNodes: SankeyNode[];
};

const AudienceBuilderRequestModal = ({
  open,
  onOpenChange,
  selectedAudiences,
  countries,
  selectedCountry,
  onCountryChange,
  platforms,
  analysis,
  sankeyLinks,
  sankeyNodes,
}: Props) => {
  const audienceCodes = selectedAudiences.map((audience) => audience.code).join(',');
  const groupedAudiences = useMemo(
    () => selectedAudiences.reduce<Record<string, SelectedAudience[]>>((acc, audience) => {
      acc[audience.category] = acc[audience.category] ?? [];
      acc[audience.category].push(audience);
      return acc;
    }, {}),
    [selectedAudiences],
  );

  const audienceOverview = useMemo(() => (
    Object.entries(groupedAudiences)
      .map(([category, audiences]) => (
        `${category}: ${audiences.map((audience) => audience.label).join(', ')}`
      ))
      .join('\n')
  ), [groupedAudiences]);

  const audienceOverviewHtml = useMemo(() => {
    const entries = Object.entries(groupedAudiences);
    if (!entries.length) return '';
    const rows = entries
      .map(([category, audiences]) => (
        `<tr><td><strong>${escapeHtml(category)}</strong></td><td>${escapeHtml(
          audiences.map((audience) => audience.label).join(', ')
        )}</td></tr>`
      ))
      .join('');
    return `
<table border="1" cellpadding="6" cellspacing="0" width="100%" style="border-collapse: collapse;">
  <tr>
    <th align="left" colspan="2" style="background-color: #f2f2f2;">Built Audience</th>
  </tr>
  ${rows}
</table>
    `.trim();
  }, [groupedAudiences]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="980px" className="builder-dialog">
        <Flex direction="column" gap="4">
          <Flex align="center" justify="between" gap="3">
            <Heading size="5">Request Audience</Heading>
            <Flex direction="column" align="end" gap="1">
                <GenericSelect
                  items={countries || []}
                  selectedItem={selectedCountry}
                  onItemChange={onCountryChange}
                  placeholder="Select country…"
                  modalTrigger={{enabled: true, label: "Country Selector", icon: <ViewHorizontalIcon/>, onTrigger: () => onOpenChange(true)}}
                  sectionLabel='Countries'
                  emptyOption={{ enabled: true, label: 'No Country', icon: <GlobeIcon/>}}
                  />
            </Flex>
          </Flex>

          <Box className="builder-dialog-layout">
            <Box className="builder-dialog-recipe">
              <Heading size="3" mb="2">Selection Summary</Heading>
              {selectedAudiences.length ? (
                <Box className="builder-recipe-group">
                  {Object.entries(groupedAudiences).map(([category, audiences]) => (
                    <Box key={category} className="builder-recipe-category-group">
                      <Text size="2" className="builder-recipe-category">
                        {category}
                      </Text>
                      <Box className="builder-recipe-items">
                        {audiences.map((audience) => (
                          <Badge key={audience.code} size="2" variant="soft">
                            {audience.label}
                          </Badge>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Text size="2" color="gray">No audiences selected.</Text>
              )}

              <AudienceBuilderAnalysis
                analysis={analysis}
                sankeyLinks={sankeyLinks}
                sankeyNodes={sankeyNodes}
                selectedCountry={selectedCountry}
                showEmptyState={false}
                compact
                showSankey={false}
              />
            </Box>

            <Box className="builder-dialog-form">
                <AudienceRequestForm
                  onBack={() => onOpenChange(false)}
                  onPlatformSelected={() => {}}
                  platforms={platforms}
                  audienceName={audienceOverview}
                  audienceCategory={Object.keys(groupedAudiences).join(', ')}
                  countrySelected={selectedCountry?.display_name}
                  requestType="builder"
                  audienceOverview={audienceOverview}
                  audienceOverviewHtml={audienceOverviewHtml}
                  audienceCodes={audienceCodes}
                />
            </Box>
          </Box>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AudienceBuilderRequestModal;
